import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { currentAccountIdAtom } from "../account";
import { hasZslAtom } from "../templates";
import { isTemplateZapId } from "../utils";

export type UsageCounts = {
  error: number;
  filtered: number;
  halted: number;
  held: number;
  pending: number;
  scheduled: number;
  skipped: number;
  success: number;
  throttled: number;
  waiting: number;
};

const defaultUsage: UsageCounts = {
  error: 0,
  filtered: 0,
  halted: 0,
  held: 0,
  pending: 0,
  scheduled: 0,
  skipped: 0,
  success: 0,
  throttled: 0,
  waiting: 0,
};

type UsageEntry = {
  counts: UsageCounts;
  date: string;
};

export const zapUsageAtomFamily_SUSPENDS = atomFamily((zapId: string) => {
  const zapUsageAtom = atom<Promise<UsageCounts | null>>(async (get) => {
    const hasZsl = get(hasZslAtom);
    if (hasZsl || isTemplateZapId(zapId)) {
      return null;
    }

    try {
      const currentAccountId = get(currentAccountIdAtom);
      const res = await fetch(
        `/api/zap-history/v2/runs/usage?account_id=${currentAccountId}&zap_ids=${zapId}&status=success`,
      );

      if (!res.ok) {
        return null;
      }

      const json = (await res.json()) as { usage: UsageEntry[] };

      return json.usage.reduce<UsageCounts>(
        (acc, usageEntry) => {
          const { counts } = usageEntry;
          for (const status in counts) {
            if (Object.prototype.hasOwnProperty.call(counts, status)) {
              const statusKey = status as keyof UsageCounts;
              if (!Object.prototype.hasOwnProperty.call(acc, statusKey)) {
                acc[statusKey] = 0;
              }
              acc[statusKey] += counts[statusKey];
            }
          }
          return acc;
        },
        { ...defaultUsage },
      );
    } catch {
      return null;
    }
  });

  zapUsageAtom.debugLabel = `zapUsageAtomFamily_SUSPENDS(${zapId})`;

  return zapUsageAtom;
});
